/**
 * The seller's Ekonomi page, one row per order (P25009-496).
 *
 * The page used to be built on Stripe: balances, balance transactions, payout
 * objects. That only ever described the card orders, and described them in
 * Stripe's words (tillgängligt, väntande, på väg). This module describes every
 * order the way the seller thinks about it, from the Sharetribe transaction
 * alone, so a card order and an own-payment order read the same:
 *
 *     Betalt  −  Provision  =  Till dig
 *
 * and three independent statuses (BR-2), each read from the transaction's
 * transitions and metadata:
 *
 *     Betalning    has the buyer paid, and is the money still taken?
 *     Leverans     has the goods left the seller?
 *     Utbetalning  has the seller been paid (card), or invoiced (own payment)?
 *
 * One order = one payout (P25009-495), so "Till dig" for a card order is the
 * amount of the one bank line that pays it out. There is no balance anywhere
 * in this file, and there must never be one: the totals above the list are the
 * visible rows added together, nothing else (BR-1, acceptance "to the öre").
 *
 * WHAT THE AMOUNTS ARE
 *   paid        payinTotal — what the buyer paid (or is to pay, own payment)
 *   vat         "varav moms", the same figure checkout and the receipt print
 *               (orderVatCore.buyerPrices)
 *   commission  the provider-commission line, a positive number
 *   toSeller    paid − commission. Since P25009-467 this is payoutTotal and the
 *               Stripe payout amount; for an own-payment order it is what stays
 *               with the seller ("Kvar hos dig") while the commission is
 *               invoiced.
 * A cancelled order counts nothing: its row shows what was paid struck out,
 * and it is left out of the totals.
 *
 * Statuses come back as KEYS plus a tone, never as text: the page turns them
 * into its translated copy, the CSV export into the same copy. A key with a
 * date (`at`) or a carrier reads that value in its message.
 *
 * CommonJS with only orderVatCore as an import, like the other *Core.js files:
 * the untranspiled API server builds the rows, and the browser bundles this for
 * the stage and totals helpers. One mapping, or the list and the CSV disagree.
 *
 * MONEY UNIT CONTRACT: integer öre in and out.
 */

const { buyerPrices } = require('./orderVatCore');

const RAIL_CARD = 'card';
const RAIL_OWN = 'own';

/* The states an order can rest in once it is an order: own payment ordered
 * and waiting, or paid by card, and everything after. Not here:
 *   - inquiry / pending-payment: a question, or a checkout never finished
 *   - payment-expired: no money ever moved, nothing to account for.
 * `canceled` IS here: a refunded order happened, and the seller looks for it. */
const ECONOMY_STATES = [
  'awaiting-off-platform-payment',
  'purchased',
  'delivered',
  'disputed',
  'received',
  'completed',
  'reviewed',
  'reviewed-by-customer',
  'reviewed-by-provider',
  'canceled',
];

const PURCHASE_PROCESS = 'default-purchase';

/* Filter buckets, as the page sketch draws them (P25009-499). Every row is in
 * exactly one, decided in this order (stageOf). A cancelled order has no chip
 * of its own: it shows under "Alla" only, struck through, so FILTER_STAGES —
 * the chips — leaves it out. */
const STAGE_CANCELED = 'canceled';
const STAGE_TODO = 'todo';
const STAGE_AWAITING_PAYMENT = 'awaitingPayment';
const STAGE_TRANSIT = 'transit';
const STAGE_PAYOUT = 'payout';
const STAGE_DONE = 'done';
const FILTER_STAGES = [STAGE_TODO, STAGE_AWAITING_PAYMENT, STAGE_TRANSIT, STAGE_PAYOUT, STAGE_DONE];
const STAGES = [...FILTER_STAGES, STAGE_CANCELED];

/* Pill tones: todo = the seller must act; wait = someone else's turn; ok =
 * done; off = ended or not applicable. */
const TONE_TODO = 'todo';
const TONE_WAIT = 'wait';
const TONE_OK = 'ok';
const TONE_OFF = 'off';

/* The process timers the deadlines are read from, restated from
 * ext/transaction-processes/default-purchase/process.edn exactly like
 * src/transactions/orderRowState.js does (that module is ESM, and this one is
 * required by the server too). Change them with the process. */
const AUTO_CANCEL_DAYS = 6; // purchased --P6D--> auto-cancel
const AUTO_RECEIVED_DAYS = 14; // delivered --P14D--> auto-mark-received

const DAY_MS = 24 * 60 * 60 * 1000;

const T = name => `transition/${name}`;
const PAID_BY_CARD = [T('confirm-payment')];
const ORDERED_OWN_PAYMENT = [
  T('request-off-platform-payment'),
  T('request-off-platform-payment-after-inquiry'),
  T('select-off-platform-payment'),
];
const PAID_DIRECT = [T('off-platform-payment-received'), T('operator-off-platform-payment-received')];
const DELIVERED = [T('mark-delivered'), T('operator-mark-delivered')];
const APPROVED = [
  T('mark-received-from-purchased'),
  T('mark-received'),
  T('auto-mark-received'),
  T('mark-received-from-disputed'),
];
const DISPUTED = [T('dispute'), T('operator-dispute')];
const CANCELED = [
  T('cancel'),
  T('auto-cancel'),
  T('cancel-from-disputed'),
  T('auto-cancel-from-disputed'),
];

const RECEIVED_STATES = [
  'received',
  'completed',
  'reviewed',
  'reviewed-by-customer',
  'reviewed-by-provider',
];

/* ------------------------------------------------------------------ dates */

/* Calendar days are Swedish days: an order at 00:30 belongs to that morning,
 * not to "yesterday" on the server's UTC clock. sv-SE formats as YYYY-MM-DD. */
let svFormatter = null;
const svDate = value => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  try {
    if (!svFormatter) {
      svFormatter = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Stockholm' });
    }
    return svFormatter.format(d);
  } catch (e) {
    return d.toISOString().slice(0, 10);
  }
};

const toIso = value => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const addDays = (iso, days) =>
  iso ? new Date(new Date(iso).getTime() + days * DAY_MS).toISOString() : null;

/* ---------------------------------------------------------------- reading */

const amountOf = money => (money && typeof money.amount === 'number' ? money.amount : 0);

const historyOf = tx => {
  const transitions = tx && tx.attributes && tx.attributes.transitions;
  return Array.isArray(transitions) ? transitions : [];
};

/* When the order last took one of these transitions, or null. */
const lastAt = (tx, names) => {
  const history = historyOf(tx);
  for (let i = history.length - 1; i >= 0; i--) {
    if (names.includes(history[i].transition)) return toIso(history[i].createdAt);
  }
  return null;
};

const hasTaken = (tx, names) => historyOf(tx).some(t => names.includes(t.transition));

/* "Kunden hämtade själv" (P25009-499): a shipping order the buyer collected
 * in person. POST /api/orders/:txId/handed-over stamps metadata.handover
 * BEFORE the seller's mark-delivered fires, the way the Frakt booking stamps
 * metadata.freight, so the order reads Upphämtad rather than Skickad. */
const HANDOVER_IN_PERSON = 'in-person';
const isHandedOverInPerson = metadata =>
  !!(metadata && metadata.handover && metadata.handover.method === HANDOVER_IN_PERSON);

/* The rail. `payOffPlatform` is written on every own-payment order, by the
 * initiate step since P25009-476 and by the old door before it; the
 * transitions are the fallback for an order whose stamp was lost. */
const railOf = tx => {
  const metadata = (tx && tx.attributes && tx.attributes.metadata) || {};
  if (metadata.payOffPlatform) return RAIL_OWN;
  if (hasTaken(tx, ORDERED_OWN_PAYMENT) || hasTaken(tx, PAID_DIRECT)) return RAIL_OWN;
  return RAIL_CARD;
};

/* The commission, as a positive amount. Reversals carry the opposite sign and
 * cancel the original, so summing every provider-commission line is right on a
 * refunded order too. */
const commissionOf = lineItems =>
  Math.abs(
    (lineItems || [])
      .filter(li => li && li.code === 'line-item/provider-commission')
      .reduce((sum, li) => sum + amountOf(li.lineTotal), 0)
  );

/* Has a cancelled card order's money gone back to the buyer? The same record
 * the cancelled-orders sweep writes and the cancellation mails wait for
 * (server/api-util/stripe/cancelMoneyState.js) — restated, because that module
 * is server-only. */
const moneyReleasedAt = metadata => {
  const m = metadata || {};
  return m.refundedAt || m.stripeVoidedAt || null;
};
const isMoneyReleased = metadata => {
  const m = metadata || {};
  if (m.payOffPlatform) return true;
  if (!m.stripeSessionId && !m.stripePaymentIntentId) return true;
  return !!(m.stripeVoidedAt || m.refundedAt || m.noPaymentToRelease);
};

/* ---------------------------------------------------------------- statuses */

const pill = (key, tone, extra) => Object.assign({ key, tone }, extra || {});

const paymentStatus = ({ rail, state, metadata, tx }) => {
  if (rail === RAIL_OWN) {
    if (state === 'awaiting-off-platform-payment') return pill('awaiting', TONE_TODO);
    if (state === 'canceled') return pill('canceled', TONE_OFF);
    return pill('paidDirect', TONE_OK, { at: lastAt(tx, PAID_DIRECT) });
  }
  if (state === 'canceled') {
    return isMoneyReleased(metadata)
      ? pill('refunded', TONE_OFF, { at: toIso(moneyReleasedAt(metadata)) })
      : pill('refundPending', TONE_WAIT);
  }
  return pill('paid', TONE_OK, { at: lastAt(tx, PAID_BY_CARD) });
};

/* The booked shipment, if any. The Frakt booking (P25009-450) stamps
 * metadata.freight before it marks the order delivered, and the stamp requires
 * a tracking number. */
const shipmentOf = metadata => {
  const freight = metadata && metadata.freight;
  if (!freight || !freight.trackingNumber) return null;
  return { carrier: freight.carrier || null, trackingNumber: freight.trackingNumber };
};

const deliveryStatus = ({ rail, state, isPickup, tx, metadata }) => {
  if (state === 'canceled') return pill('canceled', TONE_OFF);
  /* Redo för upphämtning is the buyer's turn (P25009-499): the goods are
   * ready and the buyer comes for them, so it reads like a shipment on its way,
   * not like an order the seller still has to send. */
  if (state === 'awaiting-off-platform-payment') {
    /* An own-payment pickup is often paid AT the pickup, so the goods are
     * ready; a shipment waits for the money. */
    return isPickup ? pill('readyForPickup', TONE_WAIT) : pill('shipAfterPayment', TONE_OFF);
  }
  if (state === 'purchased') {
    const paidAt = lastAt(tx, rail === RAIL_OWN ? PAID_DIRECT : PAID_BY_CARD);
    const deadline = rail === RAIL_CARD ? addDays(paidAt, AUTO_CANCEL_DAYS) : null;
    return isPickup
      ? pill('readyForPickup', TONE_WAIT, { deadline })
      : pill('toShip', TONE_TODO, { deadline });
  }
  const deliveredAt = lastAt(tx, DELIVERED);
  const shipment = shipmentOf(metadata);
  if (state === 'disputed') return pill('disputed', TONE_TODO, { at: lastAt(tx, DISPUTED) });
  if (state === 'delivered') {
    return isPickup
      ? pill('handedOver', TONE_OK, { at: deliveredAt })
      : pill('shipped', TONE_WAIT, Object.assign({ at: deliveredAt }, shipment || {}));
  }
  if (RECEIVED_STATES.includes(state)) {
    return isPickup
      ? pill('handedOver', TONE_OK, { at: deliveredAt || lastAt(tx, APPROVED) })
      : pill('delivered', TONE_OK, Object.assign({ at: lastAt(tx, APPROVED) }, shipment || {}));
  }
  return pill('unknown', TONE_OFF);
};

/* `payout` is this order's record in the Payout collection, `mirror` the
 * StripePayoutMirror row of its Stripe payout — the arrival date and the bank
 * live there, not on the record. Both are optional. */
const payoutStatus = ({ rail, state, metadata, payout, mirror }) => {
  if (rail === RAIL_OWN) {
    if (state === 'canceled') return pill('noCommission', TONE_OFF);
    if (state === 'awaiting-off-platform-payment') return pill('invoiceAfterPayment', TONE_OFF);
    /* Nothing invoices own-payment commission per order yet: the Fortnox run
     * is still to be built. When it is, it stamps metadata.commissionInvoice
     * { number, invoicedAt } and this pill turns by itself. */
    const invoice = metadata && metadata.commissionInvoice;
    if (invoice && invoice.number) {
      return pill('invoiced', TONE_OK, {
        at: toIso(invoice.invoicedAt),
        invoiceNumber: String(invoice.number),
      });
    }
    return pill('toInvoice', TONE_WAIT);
  }

  if (state === 'canceled') return pill('none', TONE_OFF);

  if (payout && payout.status === 'completed' && payout.stripePayoutId) {
    const arrival = toIso(mirror && mirror.arrivalDate);
    const landed = !mirror || mirror.status === 'paid';
    const extra = {
      at: arrival || toIso(payout.processedAt),
      payoutId: payout.stripePayoutId,
      bankLast4: (mirror && mirror.bankLast4) || null,
    };
    if (mirror && (mirror.status === 'failed' || mirror.status === 'canceled')) {
      return pill('delayed', TONE_TODO, extra);
    }
    return landed ? pill('paidOut', TONE_OK, extra) : pill('scheduled', TONE_WAIT, extra);
  }
  if (payout && payout.status === 'failed') return pill('delayed', TONE_TODO);
  if (metadata && metadata.payoutHold) return pill('onHold', TONE_WAIT);
  if (state === 'disputed') return pill('onHold', TONE_WAIT);
  if (state === 'purchased') return pill('afterDelivery', TONE_WAIT);
  if (state === 'delivered') return pill('afterApproval', TONE_WAIT);
  return pill('processing', TONE_WAIT);
};

/* ----------------------------------------------------------- confirmation */

/* The order confirmation's send log, metadata.orderConfirmationSends (BR-4):
 *   [{ at, kind: 'auto'|'manual', recipient: 'buyer'|'seller'|'other',
 *      to (masked), status: 'sent'|'bounced'|'failed', emailId }]
 *
 * The column says whether the BUYER has it. A copy the seller sent themselves
 * does not count; a resend to an address the seller typed does, because that
 * is how a bounced buyer address gets corrected. The newest such send decides:
 * a bounced auto-mail reads "Inte skickad" until a resend went through.
 *
 * With no send to the buyer in the log, the status is unknown — never "Inte
 * skickad". The buyer's and the seller's automatic mails are gated apart
 * (transaction-mail phases 1 and 2) and can come from different senders, one
 * of which may not log (Sharetribe's own notifications, an older deploy), so a
 * seller entry says nothing about the buyer. "Inte skickad" takes evidence: a
 * buyer send that bounced or failed. Orders from before the log read the same. */
const CONFIRMATION_SENT = 'sent';
const CONFIRMATION_NOT_SENT = 'notSent';
const CONFIRMATION_UNKNOWN = 'unknown';

const confirmationStatusOf = sends => {
  const log = (Array.isArray(sends) ? sends : []).filter(Boolean);
  const toBuyer = log.filter(s => s.recipient === 'buyer' || s.recipient === 'other');
  if (toBuyer.length === 0) return CONFIRMATION_UNKNOWN;
  const newest = toBuyer.reduce((a, b) => (String(b.at || '') >= String(a.at || '') ? b : a));
  return newest.status === 'sent' ? CONFIRMATION_SENT : CONFIRMATION_NOT_SENT;
};

/* "anna.sjoberg@example.se" -> "an***@example.se". Enough for a seller to
 * recognise the address and see a typo, not enough to harvest it. */
const maskEmail = email => {
  const value = String(email || '').trim();
  const at = value.lastIndexOf('@');
  if (at < 1) return value ? '***' : '';
  const local = value.slice(0, at);
  const keep = local.length <= 2 ? 1 : 2;
  return `${local.slice(0, keep)}***${value.slice(at)}`;
};

/* Manual sends made on one Swedish calendar day — the BR-4 limit counts them. */
const manualSendsOn = (sends, dayKey) =>
  (Array.isArray(sends) ? sends : []).filter(s => s && s.kind === 'manual' && svDate(s.at) === dayKey)
    .length;

/* --------------------------------------------------------------- timeline */

/* Where each `next` key sits on an order's way: payment, delivery, the
 * buyer's approval, the money to the seller. */
const PHASE_OF_NEXT = {
  awaitPayment: 0,
  shipNext: 1,
  awaitPickup: 1,
  awaitApproval: 2,
  awaitDispute: 2,
  payoutNext: 3,
  payoutScheduled: 3,
  invoiceNext: 3,
};

/* The steps still to come after `next`, which the Förlopp list prints with a
 * grey dot (P25009-499, as the sketch draws them). A card order goes delivery
 * → approval → payout. An own-payment order goes payment → delivery →
 * commission invoice: no payout waits on the buyer's approval, so the sketch
 * shows no approval step there. */
const upcomingAfter = (next, { rail, isPickup }) => {
  const phase = next ? PHASE_OF_NEXT[next.key] : undefined;
  if (phase === undefined) return [];
  const delivery = isPickup ? 'awaitPickup' : 'shipNext';
  const way =
    rail === RAIL_OWN
      ? [[0, 'awaitPayment'], [1, delivery], [3, 'invoiceNext']]
      : [[1, delivery], [2, 'awaitApproval'], [3, 'payoutNext']];
  return way.filter(([p]) => p > phase).map(([, key]) => ({ key }));
};

/* What happened, oldest first, as keys with dates; then, when the order is
 * still moving, the one step it is waiting for (`next`) and the steps after it
 * (`upcoming`). The page prints them as the Förlopp list. */
const timelineOf = ({ tx, rail, state, isPickup, payment, delivery, payoutPill }) => {
  const steps = [];
  const push = (key, at, extra) => {
    if (at) steps.push(Object.assign({ key, at }, extra || {}));
  };

  if (rail === RAIL_OWN) {
    push('ordered', lastAt(tx, ORDERED_OWN_PAYMENT) || toIso(tx.attributes.createdAt));
    push('paidDirect', lastAt(tx, PAID_DIRECT));
  } else {
    push('paid', lastAt(tx, PAID_BY_CARD) || toIso(tx.attributes.createdAt));
  }
  const deliveredAt = lastAt(tx, DELIVERED);
  const shipment = shipmentOf(tx.attributes.metadata);
  push(isPickup ? 'handedOver' : 'shipped', deliveredAt, isPickup ? null : shipment);
  push('disputed', lastAt(tx, DISPUTED));
  push('approved', lastAt(tx, APPROVED));
  push('canceled', lastAt(tx, CANCELED));
  if (payment.key === 'refunded') push('refunded', payment.at);
  if (payoutPill.key === 'paidOut') push('paidOut', payoutPill.at, { bankLast4: payoutPill.bankLast4 });
  if (payoutPill.key === 'invoiced') {
    push('commissionInvoiced', payoutPill.at || deliveredAt, {
      invoiceNumber: payoutPill.invoiceNumber,
    });
  }

  steps.sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));

  let next = null;
  if (state === 'awaiting-off-platform-payment') next = { key: 'awaitPayment' };
  else if (state === 'purchased') {
    next = { key: isPickup ? 'awaitPickup' : 'shipNext', deadline: delivery.deadline || null };
  } else if (state === 'delivered') {
    next = { key: 'awaitApproval', deadline: addDays(deliveredAt, AUTO_RECEIVED_DAYS) };
  } else if (state === 'disputed') next = { key: 'awaitDispute' };
  else if (payoutPill.key === 'scheduled') next = { key: 'payoutScheduled', at: payoutPill.at };
  else if (['processing', 'onHold', 'delayed'].includes(payoutPill.key)) next = { key: 'payoutNext' };
  else if (payoutPill.key === 'toInvoice') next = { key: 'invoiceNext' };

  return { steps, next, upcoming: upcomingAfter(next, { rail, isPickup }) };
};

/* ---------------------------------------------------------------- the row */

/* What the seller can do from the detail panel, mapped to transitions the
 * process already has (BR-3). Booking freight is its own page; a reminder is
 * a message on the order's own conversation. A shipping order the buyer came
 * for after all is handed over in person (P25009-499). */
const actionsOf = ({ state, rail, isPickup, metadata }) => {
  if (state === 'awaiting-off-platform-payment') return ['markPaid', 'remindBuyer'];
  if (state === 'purchased') {
    if (isPickup) return ['markPickedUp'];
    return shipmentOf(metadata) ? [] : ['bookFreight', 'customerPickedUp'];
  }
  return [];
};

/* The sketch's filters (P25009-499): Att göra is only what the seller must do
 * themselves, which is sending the goods. An unpaid own-payment order waits for
 * the buyer's money. Goods on their way, ready for collection or under a
 * complaint wait for the buyer. A card order then waits for its payout. */
const IN_TRANSIT = ['shipped', 'readyForPickup', 'disputed'];

const stageOf = row => {
  if (!row) return STAGE_DONE;
  if (row.payment.key === 'canceled' || row.payment.key === 'refunded' || row.payment.key === 'refundPending') {
    return STAGE_CANCELED;
  }
  if (row.delivery.key === 'toShip') return STAGE_TODO;
  if (row.payment.key === 'awaiting') return STAGE_AWAITING_PAYMENT;
  if (IN_TRANSIT.includes(row.delivery.key)) return STAGE_TRANSIT;
  if (row.rail === RAIL_CARD && row.payout.key !== 'paidOut') return STAGE_PAYOUT;
  return STAGE_DONE;
};

/**
 * One order as the Ekonomi page shows it.
 *
 * @param {object} params
 * @param {object} params.tx        transaction resource (attributes incl.
 *                                  transitions, lineItems, metadata, protectedData)
 * @param {object} [params.listing] its listing resource
 * @param {object} [params.customer] the buyer's user resource
 * @param {object} [params.payout]  the order's Payout record (card orders)
 * @param {object} [params.mirror]  that payout's StripePayoutMirror row
 * @param {boolean} [params.sellerChargesVat] the seller is a company
 * @returns {object|null} null for a transaction that is not a purchase order
 */
const economyRowOf = ({ tx, listing, customer, payout, mirror, sellerChargesVat = false } = {}) => {
  const attrs = (tx && tx.attributes) || {};
  const state = String(attrs.state || '').replace(/^state\//, '');
  const processName = String(attrs.processName || '');
  if (processName && processName !== PURCHASE_PROCESS) return null;
  if (!ECONOMY_STATES.includes(state)) return null;

  const metadata = attrs.metadata || {};
  const protectedData = attrs.protectedData || {};
  const rail = railOf(tx);
  /* A handover stamp counts only once mark-delivered has run: a stamp whose
   * transition then failed must not turn an order still to ship into a
   * pickup. */
  const isPickup =
    protectedData.deliveryMethod === 'pickup' ||
    (isHandedOverInPerson(metadata) && hasTaken(tx, DELIVERED));
  const lineItems = attrs.lineItems || [];

  const counted = state !== 'canceled';
  /* A refund adds reversal lines, which bring payinTotal down to zero. The
   * row of a cancelled order still shows what the buyer paid — struck through,
   * and out of the totals — so it reads the lines as they were charged. */
  const charged = lineItems.filter(li => li && !li.reversal);
  const paid = counted
    ? amountOf(attrs.payinTotal)
    : buyerPrices(charged, { sellerChargesVat }).totalSubunits || amountOf(attrs.payinTotal);
  const commission = commissionOf(lineItems);
  const vat = buyerPrices(counted ? lineItems : charged, { sellerChargesVat }).vatSubunits;

  const payment = paymentStatus({ rail, state, metadata, tx });
  const delivery = deliveryStatus({ rail, state, isPickup, tx, metadata });
  const payoutPill = payoutStatus({ rail, state, metadata, payout, mirror });

  const cartItems = Array.isArray(protectedData.cartItems) ? protectedData.cartItems : [];
  const sends = Array.isArray(metadata.orderConfirmationSends) ? metadata.orderConfirmationSends : [];

  const row = {
    txId: (tx.id && (tx.id.uuid || tx.id)) || null,
    orderNumber: protectedData.orderNumber || null,
    createdAt: toIso(attrs.createdAt),
    date: svDate(attrs.createdAt),
    state,
    rail,
    deliveryMethod: isPickup ? 'pickup' : protectedData.deliveryMethod || 'shipping',
    title:
      (listing && listing.attributes && listing.attributes.title) ||
      protectedData.listingTitle ||
      null,
    extraItemCount: cartItems.length,
    listingRef: (listing && listing.attributes && listing.attributes.metadata && listing.attributes.metadata.tireId) || null,
    buyerName:
      (customer && customer.attributes && customer.attributes.profile && customer.attributes.profile.displayName) ||
      (protectedData.shippingDetails && protectedData.shippingDetails.name) ||
      null,
    buyerEmailMasked: maskEmail(customer && customer.attributes && customer.attributes.email),
    currency: (attrs.payinTotal && attrs.payinTotal.currency) || 'SEK',
    paid,
    vat,
    commission: counted ? commission : 0,
    toSeller: counted ? paid - commission : 0,
    counted,
    payment,
    delivery,
    payout: payoutPill,
    shipment: shipmentOf(metadata),
    actions: actionsOf({ state, rail, isPickup, metadata }),
    documents: {
      orderConfirmation: true,
      payoutVerification: rail === RAIL_CARD && payoutPill.key === 'paidOut',
      commissionInvoice: payoutPill.key === 'invoiced' ? payoutPill.invoiceNumber : null,
    },
    confirmation: {
      status: confirmationStatusOf(sends),
      log: sends
        .filter(Boolean)
        .map(s => ({
          at: toIso(s.at),
          kind: s.kind === 'manual' ? 'manual' : 'auto',
          recipient: s.recipient || 'buyer',
          to: s.to || null,
          status: s.status || 'sent',
        })),
    },
  };
  row.timeline = timelineOf({ tx, rail, state, isPickup, payment, delivery, payoutPill });
  row.stage = stageOf(row);
  return row;
};

/* ---------------------------------------------------------------- totals */

/* The three figures above the list: the visible rows added up, refunds left
 * out. Nothing else feeds them (BR-1). */
const totalsOf = rows => {
  const counted = (rows || []).filter(r => r && r.counted);
  const paid = counted.reduce((sum, r) => sum + r.paid, 0);
  const commission = counted.reduce((sum, r) => sum + r.commission, 0);
  return {
    paid,
    commission,
    toSeller: counted.reduce((sum, r) => sum + r.toSeller, 0),
    vat: counted.reduce((sum, r) => sum + r.vat, 0),
    orderCount: counted.length,
    excludedCount: (rows || []).length - counted.length,
  };
};

const stageCounts = rows =>
  (rows || []).reduce(
    (acc, r) => Object.assign(acc, { [r.stage]: (acc[r.stage] || 0) + 1 }),
    STAGES.reduce((acc, s) => Object.assign(acc, { [s]: 0 }), {})
  );

module.exports = {
  RAIL_CARD,
  RAIL_OWN,
  ECONOMY_STATES,
  STAGE_CANCELED,
  STAGE_TODO,
  STAGE_AWAITING_PAYMENT,
  STAGE_TRANSIT,
  STAGE_PAYOUT,
  STAGE_DONE,
  STAGES,
  FILTER_STAGES,
  HANDOVER_IN_PERSON,
  isHandedOverInPerson,
  TONE_TODO,
  TONE_WAIT,
  TONE_OK,
  TONE_OFF,
  CONFIRMATION_SENT,
  CONFIRMATION_NOT_SENT,
  CONFIRMATION_UNKNOWN,
  AUTO_CANCEL_DAYS,
  AUTO_RECEIVED_DAYS,
  svDate,
  railOf,
  commissionOf,
  economyRowOf,
  stageOf,
  totalsOf,
  stageCounts,
  confirmationStatusOf,
  maskEmail,
  manualSendsOn,
};
